import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  Type,
  ViewChild,
} from '@angular/core';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  ActiveDevice,
  ContainerType,
  LayoutNames,
  TransitionPresetType,
  UiPageConfig,
  UiPagePageConfiguration,
  ZoneConfiguration,
  baseCssConfigStyle,
  cssToNgStyle,
} from '@finxone-platform/shared/sys-config-types';

import { Location } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { CloseModalPopupAction } from '@app/finxone-web-frontend/app/lib/actions/page-context.action';
import { UserProfileFetched } from '@app/finxone-web-frontend/app/lib/actions/user-profile.action';
import { PageHooksService } from '@app/finxone-web-frontend/app/lib/services/hooks-service/page-hooks-service.service';
import { ApiLoadingState } from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { toggleElementVisibilityById } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import {
  Observable,
  Subject,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  skip,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { logLoadEventToFlutter } from '../../../../lib/utils/flutter.utils';
import { formatNameAsUrl } from '../../../../lib/utils/zone-url.utils';
import { PageConfigurationProvider } from '../../page-configuration.provider';
import { pageTransition, pageTransitionPresets } from '../../parent-container/page-transitions';
import { FooterLayoutComponent } from '../../widget-layout/footer-layout/footer-layout.component';
import { MainWidgetComponent } from '../../widget-layout/main-widget/main-widget.component';
import { WidgetLayoutService } from '../../widget-layout/widget-layout.service';
import { ZonePageUrlProvider } from '../../zone-url-provider';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-mobile-container',
  templateUrl: './mobile-container.component.html',
  styleUrls: ['./mobile-container.component.scss'],
  animations: [pageTransition],
})
export class MobileContainerComponent implements OnChanges, OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @ViewChild('mainContainer', { static: false }) mainContainer!: ElementRef<HTMLDivElement>;
  @Input() navDirection: keyof TransitionPresetType = 'forward';
  @Input() zoneUrlInput = '';
  @Input() isModal = false;
  public footerLayoutHeight: number;
  public mainWidgetStyling = {};
  private _pageUrlInput: string;
  public isFooterLayoutEnabled = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public footerComponent: { component: Type<any>; injector: Injector } | null;
  public topPadding = '0px';
  public bottomPadding = '0px';
  private footerNavHeight = 0;
  public pageTransitionDone = false;
  public pageContentStyle: baseCssConfigStyle;
  public footerOuterCss = {};

  @Input() set pageUrlInput(value: string) {
    this._pageUrlInput = value;
    this.fetchAndHandlePageConfig('navigationMenu');
  }

  get pageUrlInput() {
    return this._pageUrlInput;
  }

  @Select(ApiLoadingState.getApiStateAsLoader)
  public getApiStateAsLoader$!: Observable<boolean>;

  isWorkflow: boolean;

  component: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: Type<any>;
    injector: Injector;
  };
  currentZoneConfiguration: ZoneConfiguration;
  currentPageDetail: UiPageConfig;
  hideNavigationMenu: boolean | undefined;
  public LayoutNames = LayoutNames;

  constructor(
    public configService: ConfigService,
    private injector: Injector,
    private actions: Actions,
    public hookService: PageHooksService,
    private location: Location,
    private renderer: Renderer2,
    private widgetLayoutService: WidgetLayoutService,
    private store: Store,
    public outlet: RouterOutlet,
  ) {
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;

    this.actions.pipe(ofActionDispatched(UserProfileFetched), take(1)).subscribe(() => {
      this.getCurrentZoneConfig();
    });

    this.getCurrentZoneConfig();
    this.fetchAndHandlePageConfig('navigationMenu');
    this.onGetCloseModalPopupEvent();

    logLoadEventToFlutter('mobile-container-loaded');
  }

  ngOnInit() {
    // just periodically check if padding should be updated every 500ms
    timer(0, 500)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateSafeAreaPadding();
      });

    this.subscribeToFooterHeight();
    this.onRoleChange();
    if (this.isWorkflow) {
      this.pageTransitionDone = true;
    }
  }

  /**
   * Re-fetch zone/page config when the persona changes without a page reload,
   * so footer visibility, page styles and the embedded main widget reflect the new persona.
   */
  private onRoleChange(): void {
    this.store
      .select(RoleState.getRole)
      .pipe(
        map((role) => role.role),
        filter((role): role is string => !!role),
        distinctUntilChanged(),
        skip(1), // initial load is handled by the constructor
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.getCurrentZoneConfig(true);
        this.fetchAndHandlePageConfig('navigationMenu', true);
      });
  }

  private subscribeToFooterHeight(): void {
    this.widgetLayoutService.footerHeight$
      .pipe(distinctUntilChanged(), debounceTime(100), takeUntil(this.destroy$))
      .subscribe((height: number) => {
        this.footerLayoutHeight = height;
        if (this.isFooterLayoutEnabled) {
          this.updateMainWidgetHeight();
          this.getFooterLayoutStyles();
        }
      });
  }

  private updateSafeAreaPadding() {
    // This is the in app padding needed for the safe area on mobile devices
    this.configService
      .getSafePadding()
      .pipe(take(1))
      .subscribe((padding) => {
        if (padding.isIOS) {
          // Updates the UI safe-area padding based on the device's platform and configuration settings.
          this.topPadding = `${padding.topPadding}px`;
          this.bottomPadding = `${padding.bottomPadding}px`;
        }
      });
  }

  getCurrentZoneConfig(skipNotFoundNavigation = false) {
    this.configService
      .fetchCurrentZoneConfiguration()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.currentZoneConfiguration = response;
        this.fetchAndHandlePageConfig('pageConfig', skipNotFoundNavigation);
      });
  }

  private onGetCloseModalPopupEvent() {
    this.actions
      .pipe(ofActionDispatched(CloseModalPopupAction), delay(70), takeUntil(this.destroy$))
      .subscribe((result) => {
        /**
         * this function only need to be called when previous page type is not a modal.
         */
        if (!result.isModal) {
          this.fetchAndHandlePageConfig('navigationMenu');
        }
      });
  }

  /**
   * Fetches the page configuration based on the page URL and performs the specified actions.
   * @param updateType - Specifies the type of update to perform ('pageConfig' or 'navigationMenu').
   */
  public fetchAndHandlePageConfig(
    updateType: 'pageConfig' | 'navigationMenu',
    skipNotFoundNavigation = false,
  ): void {
    if (this.pageUrlInput) {
      this.configService
        .getPageUrlPage(this.pageUrlInput, this.zoneUrlInput)
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result) {
            this.handlePageConfigByType(updateType, result);
          } else if (!skipNotFoundNavigation) {
            // never navigate back for a persona-switch rebuild: back() can cascade
            // through history into feature-redirect's reload loop
            this.pageNotFound();
          }
        });
    }
  }

  private handlePageConfigByType(updateType: 'pageConfig' | 'navigationMenu', result: UiPageConfig): void {
    switch (updateType) {
      case 'pageConfig':
        this.handlePageConfig(result);
        break;

      case 'navigationMenu':
        if (!this.component) {
          this.handlePageConfig(result);
        }
        this.handleNavigationMenu(result);
        break;
    }
  }

  /**
   * Handles page configuration updates.
   * @param result - The configuration result object.
   */
  private handlePageConfig(result: UiPageConfig): void {
    this.isFooterLayoutEnabled = result?.footerLayoutEnabled ?? false;
    if (!this.isFooterLayoutEnabled) {
      this.mainWidgetStyling = {};
    }
    this.currentPageDetail = result;
    if (this.currentPageDetail.containerType === ContainerType.PAGE)
      this.pageContentStyle = {
        ...this.configService.getPageContentStyle(result?.pageConfiguration),
        ...cssToNgStyle(this.currentPageDetail?.pageConfiguration?.outerCss ?? ''),
      };
    this.updateSubComponent();

    if (result.pageConfiguration) {
      if (result.pageConfiguration.pageHooks) {
        result.pageConfiguration.pageHooks.forEach((hook) => {
          if (hook.enabled) {
            const hookFunction = this.hookService.getHook(
              'hook' + hook.name.charAt(0).toUpperCase() + hook.name.slice(1),
            );
            if (typeof hookFunction === 'function' && !this.isWorkflow) {
              hookFunction();
            }
          }
        });
      }
    }
  }

  /**
   * Handles navigation menu visibility updates.
   * @param result - The configuration result object.
   */
  private handleNavigationMenu(result: UiPageConfig): void {
    const hideNavigationMenu = result?.pageConfiguration?.hideNavigationMenu;
    this.hideNavigationMenu = !!hideNavigationMenu;
    /**
     * Toggles the visibility of the application footer based on the navigation menu's state.
     *
     * - If the navigation menu is hidden (determined by `hideNavigationMenu`), the footer is modified to be hidden.
     * - Otherwise, the footer is displayed.
     *
     * @remarks
     * - The `toggleElementVisibilityById` function is used to set the visibility of the footer by its DOM ID `#div-app-footer`.
     * - This is added because we do have modal and page types, so it should hide both router's footer * element when hideNavigationMenu is enabled.
     */
    toggleElementVisibilityById('#div-app-footer', this.hideNavigationMenu, this.renderer);
  }

  ngOnChanges() {
    this.getCurrentZoneConfig();
  }

  /**
   * Updates the main widget height to prevent overlap with the sticky footer
   * and footer navigation bar.
   *
   * When sticky footer and body scrolling are enabled, the height is set to:
   *
   *     100vh - (footerLayoutHeight + footerNavHeight)
   *
   * This ensures the main content can scroll without overlapping the footer.
   */
  private updateMainWidgetHeight() {
    this.mainWidgetStyling = {};
    if (!this.isFooterLayoutEnabled) return;

    if (
      this.currentPageDetail?.footerConfiguration?.enableStickyFooter &&
      this.currentPageDetail?.pageConfiguration?.isBodyScrollingEnabled
    ) {
      const totalFooterHeight = !this.isModal
        ? this.footerLayoutHeight + this.footerNavHeight
        : this.footerLayoutHeight;
      this.mainWidgetStyling = {
        height: `calc(100vh - ${totalFooterHeight}px)`,
        overflow: 'scroll',
        paddingBottom: '30px',
      };
    }
  }

  private updateSubComponent() {
    this.component = {
      component: MainWidgetComponent,
      injector: this.createInjector(),
    };

    if (this.isFooterLayoutEnabled) {
      this.footerComponent = {
        component: FooterLayoutComponent,
        injector: this.createInjector(),
      };
      this.footerOuterCss = cssToNgStyle(this.currentPageDetail?.footerConfiguration?.outerCss ?? '');
    }
  }

  public setFooterNavbarHeight(height: number) {
    this.footerNavHeight = height;
  }

  private createInjector(): Injector {
    return Injector.create({
      providers: [
        {
          provide: ZonePageUrlProvider,
          useValue: new ZonePageUrlProvider(this.zoneUrlInput, this.pageUrlInput),
        },
        {
          provide: PageConfigurationProvider,
          useValue: this.currentPageDetail.pageConfiguration,
        },
      ],
      parent: this.injector,
    });
  }

  get isFooterVisible() {
    return isMobileFooterVisible(this.zoneUrlInput);
  }

  private pageNotFound() {
    if (!this.configService.getActiveConfigType().isWorkflow) {
      console.error('Page not found:', this.pageUrlInput, 'zone:', this.zoneUrlInput);
      this.goBack();
    }
  }

  public goBack() {
    this.location.back();
  }

  /**
   * Computes the combined styles for the footer layout.
   *
   * This ensures that:
   * - The footer either stays sticky at the bottom of the viewport
   *   or flows naturally with the page content, based on configuration.
   * - Appropriate padding and background are applied according to configuration.
   */
  public getFooterLayoutStyles(): baseCssConfigStyle {
    if (!this.isFooterLayoutEnabled) return {};
    const modalConfiguration = this.currentPageDetail.pageConfiguration?.modalConfiguration;
    const viewPort = this.isWorkflow ? '30vh' : '15vh';

    const base = {
      paddingBottom: this.isModal && !modalConfiguration?.cssEnabled ? '0px' : `${this.footerNavHeight}px`,
      background: this.currentPageDetail?.footerConfiguration?.enableScrollFade
        ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, var(--color-background) 20px)'
        : 'var(--color-background)',
      paddingTop: this.currentPageDetail?.footerConfiguration?.enableScrollFade ? '20px' : '',
    };
    const isSticky = !!this.currentPageDetail?.footerConfiguration?.enableStickyFooter;
    let positionStyles = {};
    positionStyles = isSticky
      ? { position: 'fixed', bottom: '0', left: '0', right: '0' }
      : { position: 'static' };

    if (this.isModal && !modalConfiguration?.cssEnabled) {
      positionStyles = {
        ...(isSticky ? { position: 'fixed' } : {}),
        bottom: `calc((100vh - ${viewPort}) - ${modalConfiguration?.modalSizeHeight}${modalConfiguration?.modalSizeMarginUnit})`,
        left: '10px',
        right: '10px',
      };
    }
    return Object.assign({}, base, positionStyles);
  }

  public getParams(outlet: RouterOutlet) {
    // Return the current page and params as the animation state
    const page = outlet?.activatedRouteData?.['page'] || outlet?.activatedRoute?.snapshot?.params?.['page'];
    const params = page ? this.getAnimationParamsForPage() : pageTransitionPresets['']; // fallback to default animation preset ['']

    return {
      value: page,
      params,
    };
  }

  private getAnimationParamsForPage() {
    const transitionPreset = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.uiSettings?.[
      ActiveDevice.MOBILE
    ]?.pageTransition;
    let preset = '';

    if (typeof transitionPreset === 'string') {
      preset = transitionPreset ?? '';
    }
    if (typeof transitionPreset === 'object') {
      preset = transitionPreset[this.navDirection] ?? '';
    }
    return pageTransitionPresets[preset];
  }

  public onTransitionDone() {
    // primary use case to only show footer after page transition finishes
    this.pageTransitionDone = true;
  }

  public onTransitionStart() {
    // Hide footer when page transition start
    this.pageTransitionDone = false;
  }
}

export function isMobileFooterVisible(zoneUrl: string): boolean {
  return (
    zoneUrl !== formatNameAsUrl(APP_ZONES.ONBOARDING) &&
    zoneUrl !== formatNameAsUrl(APP_ZONES.ORGANISATION_ONBOARDING) &&
    zoneUrl !== formatNameAsUrl(APP_ZONES.VERIFICATION) &&
    zoneUrl !== formatNameAsUrl(APP_ZONES.SIGNED_OUT_FIRST_INTRODUCTION) &&
    zoneUrl !== formatNameAsUrl(APP_ZONES.SIGNED_OUT_INTRODUCTION) &&
    zoneUrl !== formatNameAsUrl(APP_ZONES.SIGNED_OUT_FORGOT_PASSWORD) &&
    zoneUrl !== formatNameAsUrl(APP_ZONES.ORGANISATION_VERIFICATION)
  );
}
